import { useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { AUTH_CONFIG } from "../config/authConfig";

import logger from "../../../utils/secureLogger";
export const useLogin = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oktaLoading, setOktaLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [showDirectLogin, setShowDirectLogin] = useState(false);

  const { login } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const processSuccessfulLogin = async () => {
    const userData = await authService.getCompleteUserData();
    login(userData);

    const from =
      location.state?.from?.pathname || AUTH_CONFIG.ROUTES.DEFAULT_REDIRECT;
    navigate(from, { replace: true });
  };

  // ========== OKTA SAML LOGIN ==========

  const handleOktaLogin = async () => {
    setOktaLoading(true);
    setError("");
    
    try {
      logger.debug("🔐 Initiating Okta SAML login...");
      logger.debug("🔧 Using SAML provider:", authService.getOktaConfig());
      
      // Redirigir a Okta a través de Cognito Hosted UI
      const result = await authService.signInWithOkta();
      
      // If user is already authenticated, redirect to dashboard
      if (result && result.alreadyAuthenticated) {
        logger.debug("✅ User already authenticated, redirecting to dashboard...");
        // Redirect to dashboard or home page
        window.location.href = '/';
        return;
      }
      
      // If we reach here, the redirect to Okta should have happened
      setOktaLoading(false);
      
    } catch (error) {
      logger.error("❌ Okta login error:", error);
      setError("Error connecting to Okta: " + error.message);
      setOktaLoading(false);
    }
  };

  // ========== DIRECT LOGIN ==========

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      authService.validateCredentials(credentials);
      const signInResult = await authService.signIn(credentials);

      if (authService.isPasswordChangeRequired(signInResult)) {
        setNeedsPasswordChange(true);
        setLoading(false);
        return;
      }

      await processSuccessfulLogin();
    } catch (error) {
      setError(error.message || "Error signing in");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (
        !newPassword ||
        newPassword.length < AUTH_CONFIG.VALIDATION.MIN_PASSWORD_LENGTH
      ) {
        throw new Error(
          `La nueva contraseña debe tener al menos ${AUTH_CONFIG.VALIDATION.MIN_PASSWORD_LENGTH} caracteres`
        );
      }

      await authService.confirmSignIn(newPassword);
      await processSuccessfulLogin();
    } catch (error) {
      setError(error.message || "Error changing password");
    } finally {
      setLoading(false);
    }
  };

  // ========== FORM HANDLERS ==========

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpiar error cuando el usuario empiece a escribir
    if (error) {
      setError("");
    }
  };

  const resetForm = () => {
    setCredentials({ username: "", password: "" });
    setNewPassword("");
    setError("");
    setNeedsPasswordChange(false);
    setShowDirectLogin(false);
  };

  const clearError = () => {
    setError("");
  };

  const toggleDirectLogin = () => {
    setShowDirectLogin(!showDirectLogin);
    setError("");
  };

  // ========== UTILITY METHODS ==========

  const isOktaEnabled = () => {
    return authService.isOktaEnabled();
  };

  const getOktaConfig = () => {
    return authService.getOktaConfig();
  };

  return {
    // State
    credentials,
    newPassword,
    loading,
    oktaLoading,
    error,
    needsPasswordChange,
    showDirectLogin,
    
    // Direct Login Handlers
    handleSubmit,
    handlePasswordChange,
    handleChange,
    setNewPassword,
    
    // Okta Handlers
    handleOktaLogin,
    toggleDirectLogin,
    
    // Utility
    resetForm,
    clearError,
    isOktaEnabled,
    getOktaConfig,
  };
};
